import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getDashboardHomePath } from '@/utils/dashboard';
// components
import LoadingScreen from '../components/loading-screen';
//
import { PATH_AUTH, PATH_STUDENTS } from '../routes/paths';
import { useSessionContext } from './useSessionContext';

// ----------------------------------------------------------------------

AuthGuard.propTypes = {
  children: PropTypes.node,
  returnAfterLogin: PropTypes.bool,
};

// Em produção o `/auth/login` desvia pro SSO com o `next` fixo da env (a home), e o destino do app
// se perde no caminho. O SSO aceita `next` absoluto na origem do app, então, como no convite de
// afiliado, o destino vai dentro da URL dele.
const buildSSOLoginURL = (ssoLoginURL, path) => {
  const url = new URL(ssoLoginURL);
  url.searchParams.set('next', `${window.location.origin}${path}`);
  return url.toString();
};

export default function AuthGuard({ children, returnAfterLogin = false }) {
  const { isAuthenticated, isInitialized, user, SSOLoginURL } = useSessionContext();

  // Só o caminho volta depois do login: a query é por onde chegam `accessToken` e `refreshToken`
  // (`querySessionTokens`), e o `next` fica gravado na URL do login e do SSO.
  const { pathname } = useLocation();

  const [requestedLocation, setRequestedLocation] = useState(null);

  const ssoReturnURL =
    returnAfterLogin && isInitialized && !isAuthenticated && SSOLoginURL
      ? buildSSOLoginURL(SSOLoginURL, pathname)
      : null;

  useEffect(() => {
    if (ssoReturnURL) window.location.replace(ssoReturnURL);
  }, [ssoReturnURL]);

  if (!isInitialized || ssoReturnURL) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    if (returnAfterLogin) {
      return <Navigate to={`${PATH_AUTH.login}?next=${encodeURIComponent(pathname)}`} replace />;
    }

    if (pathname !== requestedLocation) {
      setRequestedLocation(pathname);
    }
    return <Navigate to={PATH_AUTH.login} />;
  }

  if (requestedLocation && pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  if (user?.registrationPending) {
    return <Navigate to={`${PATH_AUTH.register}?pending=email`} replace />;
  }

  if (user?.viewPageAs === 'student' && pathname.includes('dashboard')) {
    return <Navigate to={PATH_STUDENTS.courses} />;
  }

  if (user?.viewPageAs === 'producer' && pathname.includes('student')) {
    return <Navigate to={getDashboardHomePath(user)} />;
  }

  return <> {children} </>;
}
